import React, { useCallback, useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { Tooltip } from "@material-ui/core";
import { t } from "js/i18n";

const SeatsSelectContainer = styled.div`
    position: relative;
    width: 62px;
`;

const DisabledStyle = css`
    color: #999;
    pointer-events: none;
`;

const SeatsSelectControl = styled.div<{ $disabled?: boolean }>`
    display: flex;
    width: 62px;
    height: 38px;
    padding: 10px;
    justify-content: space-between;
    align-items: center;
    flex-shrink: 0;
    box-sizing: border-box;
    background: white;
    border: 1px solid #999;
    ${({ $disabled }) => $disabled && DisabledStyle}
`;

const SeatsInput = styled.input<{ $disabled?: boolean }>`
    all: unset;
    flex: 1 1 auto;
    min-width: 0;
    font-family: "Source Sans Pro", sans-serif;
    font-weight: 400;
    font-size: 14px;
    color: rgb(48, 49, 61);
    ${({ $disabled }) => $disabled && DisabledStyle}
`;

const SeatsArrowButton = styled.button<{ $disabled?: boolean }>`
    all: unset;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    ${({ $disabled }) => $disabled && DisabledStyle}
`;

const SeatsDropdown = styled.div<{ $showOverSeatLimit?: boolean }>`
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    width: ${({ $showOverSeatLimit }) => ($showOverSeatLimit ? "max-content" : "62px")};
    max-height: 240px;
    overflow: auto;
    border-radius: 2px;
    background: #FFF;
    box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.20);
    z-index: 2000;

    &::-webkit-scrollbar {
        width: 5px;
    }

    &::-webkit-scrollbar-thumb {
        background: #999;
    }
`;

const SeatsOptionButton = styled.button<{ $active?: boolean }>`
    all: unset;
    width: 100%;
    box-sizing: border-box;
    height: 50px;
    padding: 12px 12px 12px 16px;
    cursor: pointer;
    font-family: "Source Sans Pro";
    font-size: 14px;
    line-height: 150%; /* 13.75px */
    align-self: stretch;
    color: ${({ $active }) => ($active ? "#11A9E2" : "#333")};

    &:hover {
        background: #f7fafc;
    }

`;

const DownArrowIcon = ({ color }: { color: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M4 6L8 10L12 6" fill={color} />
    </svg>
);

export interface SeatsSelectProps {
    seatCount: number;
    maxSeats?: number;
    onSeatCountChange: (seatCount: number) => void;
    onContactSales: () => void;
    disabled?: boolean;
}

export function SeatsSelect({ seatCount, maxSeats = 20, onSeatCountChange, onContactSales, disabled = false }: SeatsSelectProps) {
    const MAX_SEATS = maxSeats;
    const seatOptions = Array.from({ length: MAX_SEATS }, (_, idx) => idx + 1);
    const [seatInputValue, setSeatInputValue] = useState<string>(String(seatCount));
    const [seatsOpen, setSeatsOpen] = useState(false);
    const [activeSeatIndex, setActiveSeatIndex] = useState<number>(0);
    const seatsSelectRef = useRef<HTMLDivElement | null>(null);
    const seatsOptionRefs = useRef<Array<HTMLButtonElement | null>>([]);
    const seatsInputRef = useRef<HTMLInputElement | null>(null);
    const wasClickedRef = useRef(false);

    useEffect(() => {
        const newValue = String(seatCount);
        const currentParsed = Number.parseInt((seatInputValue ?? "").trim(), 10);
        // Only update if the parsed numeric value is different to prevent loops
        if (currentParsed !== seatCount) {
            setSeatInputValue(newValue);
        }
    }, [seatCount]); // Only depend on seatCount, not seatInputValue to avoid loops

    // Keep active index in sync when opening the dropdown.
    useEffect(() => {
        if (!seatsOpen) return;
        const parsed = Number.parseInt((seatInputValue ?? "").trim(), 10);
        if (Number.isFinite(parsed) && parsed >= 1 && parsed <= MAX_SEATS) {
            setActiveSeatIndex(parsed - 1);
        } else {
            setActiveSeatIndex(0);
        }
    }, [seatsOpen, seatInputValue]);

    // Scroll active option into view when it changes.
    useEffect(() => {
        if (!seatsOpen) return;
        seatsOptionRefs.current[activeSeatIndex]?.scrollIntoView({ block: "nearest" });
    }, [activeSeatIndex, seatsOpen]);

    useEffect(() => {
        const onMouseDown = (event: MouseEvent) => {
            const el = seatsSelectRef.current;
            if (!el) return;
            if (!el.contains(event.target as Node)) {
                setSeatsOpen(false);
            }
        };

        document.addEventListener("mousedown", onMouseDown);
        return () => document.removeEventListener("mousedown", onMouseDown);
    }, []);

    const handleSeatsInputChange = useCallback((rawValue: string) => {
        const raw = rawValue.trim();
        const digitsOnly = raw.replace(/\D+/g, "");

        // Allow clearing to retype
        if (raw.length === 0) {
            setSeatInputValue("");
            return;
        }

        // If mixed input, keep only digits.
        if (digitsOnly.length === 0) {
            return;
        }

        setSeatInputValue(digitsOnly);

        const parsed = Number.parseInt(digitsOnly, 10);
        if (Number.isFinite(parsed)) {
            onSeatCountChange(parsed);
            if (parsed > MAX_SEATS) {
                setSeatsOpen(true);
            }
        }
    }, [onSeatCountChange, MAX_SEATS]);

    const parsedSeatInput = Number.parseInt((seatInputValue ?? "").trim(), 10);
    const isOverSeatLimit = seatCount > MAX_SEATS;
    const showOverSeatLimit = (Number.isFinite(parsedSeatInput) ? parsedSeatInput > MAX_SEATS : false) || isOverSeatLimit;

    const selectSeatValue = useCallback((value: number) => {
        setSeatInputValue(String(value));
        onSeatCountChange(value);
        setSeatsOpen(false);
    }, [onSeatCountChange]);

    const openSeatsMenuSoon = () => {
        // Opening the dropdown during the focus event can interfere with mouse-based caret placement
        // in a controlled input. Deferring by a tick preserves expected cursor behavior.
        window.setTimeout(() => setSeatsOpen(true), 0);
    };

    // Handle keyboard navigation for the seats dropdown
    const handleSeatsKeyboard = useCallback((e: KeyboardEvent | React.KeyboardEvent) => {
        const key = e.key;

        // Arrow keys: navigate through options
        if (key === "ArrowDown" || key === "ArrowUp") {
            e.preventDefault();
            e.stopPropagation();

            if (!seatsOpen) {
                setSeatsOpen(true);
                return;
            }

            if (showOverSeatLimit) {
                return; // Can't navigate when over limit
            }

            const delta = key === "ArrowDown" ? 1 : -1;
            setActiveSeatIndex(prev => Math.max(0, Math.min(MAX_SEATS - 1, prev + delta)));
            return;
        }

        // Enter: select the active option
        if (key === "Enter") {
            if (!seatsOpen) return;

            e.preventDefault();
            e.stopPropagation();

            if (showOverSeatLimit) {
                onContactSales();
                setSeatsOpen(false);
                return;
            }

            const selectedValue = seatOptions[activeSeatIndex] ?? 1;
            selectSeatValue(selectedValue);
            return;
        }

        // Escape: close the dropdown
        if (key === "Escape") {
            if (!seatsOpen) return;

            e.preventDefault();
            e.stopPropagation();
            setSeatsOpen(false);
        }
    }, [seatsOpen, showOverSeatLimit, activeSeatIndex, seatOptions, MAX_SEATS, onContactSales, selectSeatValue]);

    // Global keyboard handler: allows navigation when dropdown is open but input isn't focused
    useEffect(() => {
        if (!seatsOpen) return;

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.defaultPrevented) return;

            const activeEl = document.activeElement;
            // Skip if another input is focused (let it handle its own keyboard events)
            if (activeEl instanceof HTMLInputElement && activeEl !== seatsInputRef.current) {
                return;
            }
            // Skip if seats input is focused (it has its own onKeyDown handler)
            if (activeEl === seatsInputRef.current) {
                return;
            }

            handleSeatsKeyboard(event);
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [seatsOpen, handleSeatsKeyboard]);

    return (
        <Tooltip title={disabled ? t("Cannot change seat count until you reactivate") : undefined}
            disableHoverListener={!disabled}
            disableFocusListener={!disabled}
        >
            <SeatsSelectContainer ref={seatsSelectRef} data-testid="seats-select">
                <SeatsSelectControl $disabled={disabled}>
                    <SeatsInput
                        $disabled={disabled}
                        ref={seatsInputRef}
                        data-testid="seats-select-input"
                        value={seatInputValue}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        // We allow up to 2 digits for the seat count
                        // Anything above 20 will show the contact sales option
                        maxLength={2}
                        onMouseDown={() => {
                            wasClickedRef.current = true;
                        }}
                        onFocus={() => {
                            // If focus came from keyboard (tabbing), open on focus.
                            // If it came from mouse click, onClick will handle it to preserve caret placement.
                            if (!seatsOpen && !wasClickedRef.current) {
                                openSeatsMenuSoon();
                            }
                            wasClickedRef.current = false; // Reset for next interaction
                        }}
                        onClick={() => {
                            if (!seatsOpen) {
                                openSeatsMenuSoon();
                            }
                        }}
                        onKeyDown={handleSeatsKeyboard}
                        onChange={e => handleSeatsInputChange(e.target.value)}
                        onBlur={() => {
                            const next = (seatInputValue ?? "").trim();
                            if (!next) {
                                setSeatInputValue("1");
                                onSeatCountChange(1);
                            }
                        }}
                    />
                    <SeatsArrowButton
                        type="button"
                        $disabled={disabled}
                        onMouseDown={e => e.preventDefault()} // keep focus in input
                        onClick={() => setSeatsOpen(seatsOpen => !seatsOpen)}
                    >
                        <DownArrowIcon color={disabled ? "#999" : "#666"} />
                    </SeatsArrowButton>
                </SeatsSelectControl>

                {seatsOpen && (
                    <SeatsDropdown $showOverSeatLimit={showOverSeatLimit}>
                        {showOverSeatLimit ? (
                            <SeatsOptionButton
                                type="button"
                                $active={true}
                                onMouseDown={e => e.preventDefault()}
                                onClick={() => {
                                    onContactSales();
                                    setSeatsOpen(false);
                                }}
                            >
                                {t("For {{maxSeats}}+ seats, contact sales", { maxSeats: MAX_SEATS })}
                            </SeatsOptionButton>
                        ) : (
                            seatOptions.map(value => (
                                <SeatsOptionButton
                                    key={value}
                                    type="button"
                                    $active={seatOptions[activeSeatIndex] === value}
                                    onMouseDown={e => e.preventDefault()}
                                    onClick={() => {
                                        selectSeatValue(value);
                                    }}
                                    ref={el => {
                                        seatsOptionRefs.current[value - 1] = el;
                                    }}
                                    data-testid={`seats-select-option-${value}`}
                                >
                                    {value}
                                </SeatsOptionButton>
                            ))
                        )}
                    </SeatsDropdown>
                )}
            </SeatsSelectContainer>
        </Tooltip>
    );
}

